import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useUIStore } from '@/store/uiStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { BottomTabBar } from './BottomTabBar';
import { AIPanel } from './AIPanel';
import { VerificationBanner } from './VerificationBanner';
import { FloatingAIButton } from '@/components/ai/FloatingAIButton';
import { AISlidePanel } from '@/components/ai/AISlidePanel';
import styles from './layout.module.css';

class PageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; errorPath: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorPath: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, errorPath: window.location.pathname };
  }
  componentDidUpdate() {
    if (this.state.hasError && window.location.pathname !== this.state.errorPath) {
      this.setState({ hasError: false, error: null, errorPath: '' });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorCard}>
            <h2 className={styles.errorTitle}>This page encountered an error</h2>
            <p className={styles.errorMessage}>{this.state.error?.message}</p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className={styles.errorRetryBtn}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const AppShell: React.FC = () => {
  const {
    sidebarOpen,
    aiPanelOpen,
    aiSlideOpen,
    mobileMenuOpen,
    setMobileMenuOpen,
  } = useUIStore();

  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, setMobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen, isMobile]);

  return (
    <div className={styles.appShell}>
      <VerificationBanner />
      <TopBar />

      <div className={styles.appMain}>
        {/* Desktop Sidebar */}
        <div className={styles.desktopSidebar}>
          <Sidebar />
        </div>

        {/* Mobile Sidebar Drawer */}
        {isMobile && mobileMenuOpen && (
          <div
            className={styles.mobileDrawerBackdrop}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}
        {isMobile && (
          <div
            className={`${styles.mobileDrawer} ${mobileMenuOpen ? styles.mobileDrawerOpen : styles.mobileDrawerClosed}`}
            aria-label="Navigation drawer"
          >
            <Sidebar />
          </div>
        )}

        {/* Tablet Sidebar */}
        {!isMobile && (
          <div className={`${styles.tabletSidebar} ${sidebarOpen ? styles.tabletSidebarOpen : styles.tabletSidebarClosed}`}>
            <Sidebar />
          </div>
        )}

        {/* Main content */}
        <main id="main-content" className={styles.mainContent}>
          <div className={styles.mainContentInner}>
            <PageErrorBoundary>
              <Outlet />
            </PageErrorBoundary>
          </div>
          <FloatingAIButton />
          {aiSlideOpen && <AISlidePanel />}
        </main>

        {/* AI Panel — desktop */}
        {aiPanelOpen && (
          <div className={styles.desktopAIPanel}>
            <AIPanel />
          </div>
        )}
        {/* AI Panel — mobile */}
        {aiPanelOpen && isMobile && <AIPanel />}
      </div>

      <BottomTabBar />
    </div>
  );
};
